import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  ApiError,
  api,
  type TemplateFull,
  type TemplateStatus,
  type TemplateSummary
} from "../api/client";
import { Pagination } from "../components/Pagination";

// Catalog page size. A status can hold more templates than one page (the caller's
// own plus every other customer's published ones), so the list is offset-paginated
// rather than capped — otherwise older owned templates get buried behind newer
// public ones and become unmanageable.
const PAGE_SIZE = 25;

// Phase 5 template library UI. Templates move through private → anchored →
// published; anchoring signs a registration-key proof over the template's
// server-computed profile_hash, and publishing makes it visible in the public
// catalog. Authoring is a raw WorkflowProfile JSON editor: the backend does
// the full shape validation and returns per-field messages (surfaced from
// ApiError.details), so the browser does not re-implement that logic.

// A trimmed, known-valid WorkflowProfile used by the "Load example" button.
// Derived from the invoice_payment demo profile. The profile_id is scoped to the
// author's namespace (or a "your-namespace/" placeholder when unclaimed), because
// a published template's profile_id must start with the owner's namespace. Kept
// in sync with what the backend validator (profileShapeErrors) accepts.
function exampleProfile(namespace: string | null): string {
  const scope = namespace ?? "your-namespace";
  return `{
  "profile_id": "${scope}/invoice_payment.v0.1",
  "description": "Example workflow template. Rename profile_id and edit the actions for your own workflow.",
  "receipt_mode": "profile_constrained",
  "parameters": {
    "max_amount": { "type": "number", "required": true },
    "vendor_allowlist": { "type": "array", "required": false }
  },
  "allowed_actions": [
    "task_created",
    "policy_checked",
    "llm_invoice_reviewed",
    "human_approval_received",
    "payment_instruction_created"
  ],
  "required_actions": [
    "task_created",
    "policy_checked",
    "llm_invoice_reviewed",
    "payment_instruction_created"
  ],
  "allowed_transitions": [
    ["START", "task_created"],
    ["task_created", "policy_checked"],
    ["policy_checked", "llm_invoice_reviewed"],
    ["llm_invoice_reviewed", "human_approval_received"],
    ["human_approval_received", "payment_instruction_created"],
    ["llm_invoice_reviewed", "payment_instruction_created"]
  ],
  "required_attestations": {
    "human_approval_received": ["human_signature"],
    "payment_instruction_created": ["agent_signature", "witness_signature"]
  },
  "allowed_final_actions": ["payment_instruction_created"]
}
`;
}

type StatusFilter = "all" | TemplateStatus;

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "private", label: "Private" },
  { value: "anchored", label: "Anchored" },
  { value: "published", label: "Published" }
];

function statusBadgeClass(status: TemplateStatus): string {
  if (status === "published") return "badge badge-registered";
  return "badge";
}

export function TemplatesPage() {
  const [templates, setTemplates] = useState<TemplateSummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1); // 1-based
  const [creating, setCreating] = useState(false);
  // The account's publisher namespace (migration 0027), or null if unclaimed.
  // Publishing requires it, so it's surfaced at the top of the page and threaded
  // into the create example + the per-row publish control. A load FAILURE is kept
  // distinct from a successful "unclaimed" (null): on failure the panel offers a
  // retry, and the publish control is NOT disabled (the namespace is unknown, not
  // known-absent) so an account that already owns one can still publish — the
  // backend guards regardless.
  const [namespace, setNamespace] = useState<string | null>(null);
  const [namespaceLoaded, setNamespaceLoaded] = useState(false);
  const [namespaceError, setNamespaceError] = useState<string | null>(null);
  // Bumping this re-runs the loader (Retry).
  const [namespaceReload, setNamespaceReload] = useState(0);
  // True only when we definitively know the account's namespace state (the load
  // succeeded). A failed/pending load leaves this false so publish stays enabled.
  const namespaceKnown = namespaceLoaded && namespaceError === null;

  useEffect(() => {
    let cancelled = false;
    setNamespaceLoaded(false);
    setNamespaceError(null);
    api
      .getNamespace()
      .then((r) => {
        if (!cancelled) {
          setNamespace(r.namespace);
          setNamespaceLoaded(true);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setNamespaceError(
            err instanceof ApiError ? err.message : "Could not load your namespace."
          );
          setNamespaceLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [namespaceReload]);

  // Mirrors the currently-selected query. A manual refresh (load, called after a
  // mutation or create) captures the filter/page it was kicked off for; if the
  // user switches filter/page before it resolves, this lets the stale response
  // be discarded instead of clobbering the current view. The effect fetch has
  // its own `cancelled` guard, so it does not need this.
  const queryRef = useRef({ statusFilter, page });
  useEffect(() => {
    queryRef.current = { statusFilter, page };
  }, [statusFilter, page]);

  async function load(): Promise<void> {
    const q = { statusFilter, page };
    const result = await api.listTemplates({
      status: q.statusFilter === "all" ? undefined : q.statusFilter,
      limit: PAGE_SIZE,
      offset: (q.page - 1) * PAGE_SIZE
    });
    // Superseded: the user changed filter/page while this refresh was in flight.
    // Dropping it keeps the displayed rows consistent with the active controls.
    if (queryRef.current.statusFilter !== q.statusFilter || queryRef.current.page !== q.page) {
      return;
    }
    // A lifecycle mutation (publish/revoke) can shrink the result set so the
    // current page is now past the end — the refresh would then show an empty
    // out-of-range page with no way back. Snap to the last valid page; the
    // effect refetches at the corrected offset.
    const maxPage = Math.max(1, Math.ceil(result.total / PAGE_SIZE));
    if (q.page > maxPage) {
      setPage(maxPage);
      return;
    }
    setTemplates(result.templates);
    setTotal(result.total);
    setLoadError(null);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Drop the previous query's rows/error while the new filter/page loads. The
    // loading branch only renders when `templates` is null, so without this the
    // old rows would show under the newly selected filter/page (and persist if
    // the new request fails). Clearing ties the displayed catalog to the active
    // query.
    setTemplates(null);
    setTotal(0);
    setLoadError(null);
    (async () => {
      try {
        const result = await api.listTemplates({
          status: statusFilter === "all" ? undefined : statusFilter,
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE
        });
        if (!cancelled) {
          setTemplates(result.templates);
          setTotal(result.total);
          setLoadError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof ApiError ? err.message : "Could not load templates.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [statusFilter, page]);

  function retryLoad() {
    setLoading(true);
    load()
      .catch((err) =>
        setLoadError(err instanceof ApiError ? err.message : "Could not load templates.")
      )
      .finally(() => setLoading(false));
  }

  const rows = templates ?? [];

  return (
    <>
      <div className="panel">
        <div className="section-header">
          <h2 className="section-title">Templates</h2>
          <button
            className="btn"
            onClick={() => setCreating(true)}
            disabled={loading && !templates}
          >
            New template
          </button>
        </div>
        <p className="muted" style={{ marginTop: 0 }}>
          Workflow templates define the allowed actions, transitions, and conditional requirements a{" "}
          <code className="muted-code">profile_constrained</code> receipt is checked against. Author
          a template as a WorkflowProfile document, <strong>anchor</strong> it (a registration-key
          proof over its hash, verifiable offline), then <strong>publish</strong> it to the public
          catalog. Published templates are visible to everyone; private and anchored templates are
          visible only to you.
        </p>
      </div>

      <NamespacePanel
        namespace={namespace}
        loaded={namespaceLoaded}
        error={namespaceError}
        onRetry={() => setNamespaceReload((n) => n + 1)}
        onClaimed={(ns) => {
          setNamespace(ns);
          setNamespaceError(null);
        }}
      />

      <div className="panel">
        <div
          className="section-header"
          style={{ marginTop: 0, marginBottom: 8, justifyContent: "flex-end", gap: 8 }}
        >
          <label
            className="muted"
            style={{ display: "flex", gap: 6, alignItems: "center" }}
            htmlFor="template-status-filter"
          >
            Status
            <select
              id="template-status-filter"
              className="input"
              style={{ width: "auto" }}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as StatusFilter);
                setPage(1);
              }}
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {loading && !templates ? (
          <p className="muted">Loading templates…</p>
        ) : loadError && !templates ? (
          <div>
            <div className="error">{loadError}</div>
            <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={retryLoad}>
              Retry
            </button>
          </div>
        ) : rows.length === 0 ? (
          <p className="empty-state">
            {statusFilter === "all"
              ? 'No templates yet. Click "New template" to author your first WorkflowProfile.'
              : `No ${statusFilter} templates.`}
          </p>
        ) : (
          <>
            <div className="key-list">
              {rows.map((t) => (
                <TemplateRow
                  key={t.template_id}
                  template={t}
                  namespace={namespace}
                  namespaceKnown={namespaceKnown}
                  onChanged={load}
                />
              ))}
            </div>
            <Pagination
              currentPage={page}
              totalItems={total}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </>
        )}
        {loadError && templates ? (
          <div style={{ marginTop: 12 }}>
            <div className="error">{loadError}</div>
            <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={retryLoad}>
              Retry
            </button>
          </div>
        ) : null}
      </div>

      {creating ? (
        <CreateModal
          namespace={namespace}
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            // A new template is private and sorts to the first page, so it is
            // only visible under the "all" filter on page 1. If the user created
            // from another filter/page, switch there and let the list effect
            // load it (otherwise the new template would appear lost). If already
            // on the default view, refresh in place and surface a create-specific
            // message if that refresh fails — the create itself succeeded.
            if (statusFilter !== "all" || page !== 1) {
              setStatusFilter("all");
              setPage(1);
              return;
            }
            try {
              await load();
            } catch (err) {
              setLoadError(
                err instanceof ApiError
                  ? `Template created, but the list could not refresh (${err.message}). Reload to see it.`
                  : "Template created, but the list could not refresh. Reload to see it."
              );
            }
          }}
        />
      ) : null}
    </>
  );
}

function TemplateRow(props: {
  template: TemplateSummary;
  namespace: string | null;
  // True only when the namespace state is known (its load succeeded). When
  // unknown (load failed/pending), publish is left enabled — the backend guards.
  namespaceKnown: boolean;
  onChanged: () => Promise<void>;
}) {
  const t = props.template;
  const [working, setWorking] = useState<null | "anchor" | "publish" | "revoke">(null);
  const [confirm, setConfirm] = useState<null | "publish" | "revoke">(null);
  const [error, setError] = useState<string | null>(null);
  const [full, setFull] = useState<TemplateFull | null>(null);
  const [loadingFull, setLoadingFull] = useState(false);

  async function act(
    kind: "anchor" | "publish" | "revoke",
    fn: () => Promise<unknown>
  ): Promise<void> {
    setWorking(kind);
    setError(null);
    try {
      await fn();
    } catch (err) {
      // The mutation itself failed — safe to retry.
      setError(err instanceof ApiError ? err.message : `Could not ${kind} this template.`);
      setWorking(null);
      setConfirm(null);
      return;
    }
    // The mutation succeeded. A failure refreshing the catalog afterwards must
    // NOT be reported as a failed mutation — retrying a revoke that already
    // succeeded would just 404. Surface the refresh problem separately and keep
    // the success.
    setWorking(null);
    setConfirm(null);
    try {
      await props.onChanged();
    } catch {
      setError(`${kind} succeeded, but the list could not refresh. Reload to see the latest.`);
    }
  }

  async function toggleJson() {
    if (full) {
      setFull(null);
      return;
    }
    setLoadingFull(true);
    setError(null);
    try {
      const result = await api.getTemplate(t.template_id);
      setFull(result.template);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load the template document.");
    } finally {
      setLoadingFull(false);
    }
  }

  return (
    <div className="key-item">
      <div className="key-item-main">
        <div className="key-item-header">
          <span className="key-item-label">{t.profile_id}</span>
          <span className={statusBadgeClass(t.status)} aria-label={`Status: ${t.status}`}>
            {t.status}
          </span>
          <span className="badge" aria-label={`Tier: ${t.tier}`}>
            {t.tier}
          </span>
          {t.author_signed ? (
            <span className="badge badge-registered" aria-label="Author signed">
              author signed
            </span>
          ) : null}
        </div>
        <div className="key-item-meta">
          <span className="key-item-prefix" title={t.template_id}>
            {t.template_id}
          </span>
          <span title={t.profile_hash}>{t.profile_hash.slice(0, "sha256:".length + 16)}…</span>
          <span title={t.created_at}>Created {formatRelative(t.created_at)}</span>
          {t.published_at ? (
            <span title={t.published_at}>Published {formatRelative(t.published_at)}</span>
          ) : null}
          {t.anchor ? (
            <span title={`Anchored ${t.anchor.anchored_at} (key ${t.anchor.key_id})`}>
              Anchored {formatRelative(t.anchor.anchored_at)}
            </span>
          ) : null}
        </div>
        {full ? (
          <pre className="key-value" style={{ whiteSpace: "pre-wrap", marginTop: 8 }}>
            {JSON.stringify(full.profile, null, 2)}
          </pre>
        ) : null}
        {error ? (
          <div className="error" style={{ marginTop: 8 }}>
            {error}
          </div>
        ) : null}
      </div>
      <div className="key-item-actions">
        <button className="btn btn-secondary" onClick={toggleJson} disabled={loadingFull}>
          {loadingFull ? "Loading…" : full ? "Hide JSON" : "View JSON"}
        </button>

        {/* Anchor / publish / revoke mutate the template and are owner-only.
            A public catalog row (another customer's published template) is not
            owned, so these are hidden; the backend would reject them with 404
            anyway. Anyone can still View JSON above.

            Gate on `!== false`, not truthiness: `owned` is a field the
            dashboard-api added alongside this UI. The dashboard (Cloudflare)
            auto-deploys on merge while dashboard-api needs a manual redeploy,
            so during that window `owned` can be absent. Treat absent as
            "assume owned" (the pre-owned behavior — controls shown, backend
            still guards with 404) rather than hiding controls from the owner;
            once dashboard-api ships `owned`, an explicit false hides them. */}
        {t.owned !== false ? (
          <>
            {t.status === "private" ? (
              <button
                className="btn btn-secondary"
                disabled={working !== null}
                onClick={() => void act("anchor", () => api.anchorTemplate(t.template_id))}
              >
                {working === "anchor" ? "Anchoring…" : "Anchor"}
              </button>
            ) : null}

            {t.status === "private" || t.status === "anchored" ? (
              confirm === "publish" ? (
                <>
                  <button
                    className="btn"
                    disabled={working !== null}
                    onClick={() =>
                      void act("publish", () => api.anchorTemplate(t.template_id, true))
                    }
                  >
                    {working === "publish" ? "Publishing…" : "Confirm publish"}
                  </button>
                  <button
                    className="btn btn-secondary"
                    disabled={working !== null}
                    onClick={() => setConfirm(null)}
                  >
                    Cancel
                  </button>
                </>
              ) : props.namespaceKnown && props.namespace === null ? (
                // Publishing requires a claimed namespace (the id must be scoped
                // to it). Disable rather than let the publish fail server-side,
                // and point the owner at the panel above. Only when we KNOW the
                // account has none — an unknown (failed-load) state leaves publish
                // enabled and lets the backend guard.
                <button
                  className="btn btn-secondary"
                  disabled
                  title="Claim a publisher namespace above before publishing"
                >
                  Publish
                </button>
              ) : (
                <button
                  className="btn btn-secondary"
                  disabled={working !== null}
                  onClick={() => setConfirm("publish")}
                  title="Anchors (if needed) and makes this template publicly visible"
                >
                  Publish
                </button>
              )
            ) : null}

            {confirm === "revoke" ? (
              <>
                <button
                  className="btn btn-danger"
                  disabled={working !== null}
                  onClick={() => void act("revoke", () => api.revokeTemplate(t.template_id))}
                >
                  {working === "revoke" ? "Revoking…" : "Confirm revoke"}
                </button>
                <button
                  className="btn btn-secondary"
                  disabled={working !== null}
                  onClick={() => setConfirm(null)}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                className="btn btn-secondary"
                disabled={working !== null}
                onClick={() => setConfirm("revoke")}
              >
                Revoke
              </button>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

function CreateModal(props: {
  namespace: string | null;
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<string[] | null>(null);

  async function submit() {
    setError(null);
    setDetails(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (err) {
      setError(
        `The template is not valid JSON: ${err instanceof Error ? err.message : "parse error"}`
      );
      return;
    }
    setSubmitting(true);
    try {
      await api.createTemplate(parsed);
      await props.onCreated();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setDetails(err.details && err.details.length > 0 ? err.details : null);
      } else {
        setError("Could not reach Sequesign. Please try again.");
      }
      setSubmitting(false);
    }
  }

  return (
    <ModalShell>
      <h2 className="section-title">New template</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Paste a WorkflowProfile document. It is created as a <strong>private</strong> template; you
        can anchor and publish it afterward. The <code className="muted-code">profile_hash</code> is
        computed by Sequesign, so you don't set it here. To publish, the{" "}
        <code className="muted-code">profile_id</code> must be scoped to your namespace (
        <code className="muted-code">{(props.namespace ?? "your-namespace") + "/…"}</code>
        ).
      </p>
      <div className="field">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <label className="label" htmlFor="template-json">
            WorkflowProfile (JSON)
          </label>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setText(exampleProfile(props.namespace));
              setError(null);
              setDetails(null);
            }}
            disabled={submitting}
          >
            Load example
          </button>
        </div>
        <textarea
          id="template-json"
          className="input"
          style={{ minHeight: 320, fontFamily: "var(--font-mono, monospace)", whiteSpace: "pre" }}
          spellCheck={false}
          autoFocus
          value={text}
          placeholder='{ "profile_id": "…", "allowed_actions": […], "allowed_transitions": [["START", "…"]] }'
          onChange={(e) => setText(e.target.value)}
          disabled={submitting}
        />
      </div>
      {error ? <div className="error">{error}</div> : null}
      {details ? (
        <ul className="error" style={{ marginTop: 8, paddingLeft: 20 }}>
          {details.map((d, i) => (
            <li key={i}>{d}</li>
          ))}
        </ul>
      ) : null}
      <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
        <button className="btn btn-secondary" onClick={props.onClose} disabled={submitting}>
          Cancel
        </button>
        <button
          className="btn"
          disabled={submitting || text.trim().length === 0}
          onClick={() => void submit()}
        >
          {submitting ? "Creating…" : "Create template"}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell(props: { children: ReactNode }) {
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="modal" style={{ maxWidth: 680, width: "90%" }}>
        {props.children}
      </div>
    </div>
  );
}

// Publisher-namespace panel (migration 0027). Shows the account's claimed handle
// (or an unclaimed state) and opens a modal to claim/rename it. A published
// template's profile_id must be scoped to this handle, so it gates the publish
// controls below.
function NamespacePanel(props: {
  namespace: string | null;
  loaded: boolean;
  error: string | null;
  onRetry: () => void;
  onClaimed: (namespace: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const scope = props.namespace ?? "your-namespace";
  // A load failure must not be shown as "unclaimed" (it would wrongly imply the
  // account has no namespace and lock publishing) — show an error + Retry, and
  // disable the claim button until we actually know the current state.
  const canEdit = props.loaded && props.error === null;
  return (
    <div className="panel">
      <div className="section-header">
        <h2 className="section-title">Publisher namespace</h2>
        <button className="btn btn-secondary" onClick={() => setEditing(true)} disabled={!canEdit}>
          {props.namespace ? "Change" : "Claim namespace"}
        </button>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        Your namespace is a unique handle that scopes the templates you publish. A published
        template's <code className="muted-code">profile_id</code> must start with it (e.g.{" "}
        <code className="muted-code">{scope}/invoice_payment.v0.1</code>), so no two accounts can
        publish the same id. You need one before you can publish.
      </p>
      {!props.loaded ? (
        <p className="muted">Loading…</p>
      ) : props.error ? (
        <div>
          <div className="error">{props.error}</div>
          <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={props.onRetry}>
            Retry
          </button>
        </div>
      ) : props.namespace ? (
        <div className="key-item-header">
          <span className="key-item-label">{props.namespace}</span>
          <span className="badge badge-registered">claimed</span>
        </div>
      ) : (
        <p className="empty-state">No namespace yet. Claim one to publish templates.</p>
      )}
      {editing ? (
        <NamespaceModal
          current={props.namespace}
          onClose={() => setEditing(false)}
          onClaimed={(ns) => {
            props.onClaimed(ns);
            setEditing(false);
          }}
        />
      ) : null}
    </div>
  );
}

function NamespaceModal(props: {
  current: string | null;
  onClose: () => void;
  onClaimed: (namespace: string) => void;
}) {
  const [value, setValue] = useState(props.current ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    setSubmitting(true);
    try {
      const result = await api.setNamespace(value.trim());
      props.onClaimed(result.namespace);
    } catch (err) {
      // Surfaces invalid_namespace (400), namespace_taken (409), and
      // namespace_locked (409) with the backend's actionable message.
      setError(err instanceof ApiError ? err.message : "Could not save the namespace.");
      setSubmitting(false);
    }
  }

  return (
    <ModalShell>
      <h2 className="section-title">{props.current ? "Change namespace" : "Claim namespace"}</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        3–40 lowercase letters, digits, and single interior hyphens (e.g.{" "}
        <code className="muted-code">acme</code> or <code className="muted-code">acme-corp</code>).
        It becomes the prefix for every template you publish.
        {props.current ? " You can only rename it while you have no published templates." : ""}
      </p>
      <div className="field">
        <label className="label" htmlFor="namespace-input">
          Namespace
        </label>
        <input
          id="namespace-input"
          className="input"
          autoFocus
          spellCheck={false}
          value={value}
          placeholder="acme-corp"
          disabled={submitting}
          onChange={(e) => setValue(e.target.value)}
        />
      </div>
      {error ? <div className="error">{error}</div> : null}
      <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
        <button className="btn btn-secondary" onClick={props.onClose} disabled={submitting}>
          Cancel
        </button>
        <button
          className="btn"
          disabled={submitting || value.trim().length === 0}
          onClick={() => void submit()}
        >
          {submitting ? "Saving…" : props.current ? "Save namespace" : "Claim namespace"}
        </button>
      </div>
    </ModalShell>
  );
}

// Local relative-time formatter (mirrors RegistrationsPage/ApiKeysPage);
// returns the raw value on a parse failure so the tooltip still shows the
// absolute timestamp.
function formatRelative(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  const diff = Date.now() - parsed;
  if (diff < 0) return "just now";
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} ${days === 1 ? "day" : "days"} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${months === 1 ? "month" : "months"} ago`;
  const years = Math.floor(days / 365);
  return `${years} ${years === 1 ? "year" : "years"} ago`;
}
